import { Arrays } from "../../tools/Arrays";
import { LexerEvent } from "../lexer/LexerEvent";
import { LexerMatcherLibrary } from "../lexer/LexerMatcherLibrary";
import { WordLexer } from "../lexer/WordLexer";

export class TextLine
{
  words:LexerEvent[] = [];
  lineLength = 0;

  get lineContent():string
  {
    let matches:string[] = this.words.map( w => w.match );
    return matches.join( "" );
  }

  removeAt( index:number )
  {
    let e = this.words[ index ];
    this.lineLength -= e.match.length;
    Arrays.removeAt( this.words, index );

    return e;
  }

  removeLast()
  {
    return this.removeAt( this.words.length -1 );;
  }

  removeFirst()
  {
    return this.removeAt( 0 );
  }

  insertAtStart( e:LexerEvent )
  {
    Arrays.insert( this.words, e, 0 );
    this.lineLength += e.match.length;
  }

  insertAtEnd( e:LexerEvent )
  {
    this.words.push( e );
    this.lineLength += e.match.length;
  }
}

export class TextAlignment
{
  _lines:TextLine[] = [];
  _maxChars = 40;


  align( text:string )
  {
    let lexer = new WordLexer();
    let words = lexer.lexTokens( text );
    let anySet = new Set<string>();
    anySet.add( LexerMatcherLibrary.ANY_SYMBOL_MATCHER.type ); 
    words = lexer.compress( words, anySet );
    lexer.resolveMatches( text, words );

    let line = new TextLine();

    for ( let i = 0; i < words.length; i++ )
    {
      if ( words[ i ].isDoneOrError )
      {
        continue;
      }

      let willOverflow = words[ i ].match.length + line.lineLength > this._maxChars;

      if ( willOverflow )
      {
        this._lines.push( line );
        line = new TextLine();
      }
      
      line.insertAtEnd( words[ i ] );
      
    }

    this._lines.push( line );

  }

  getLineSignature()
  {
    return this._lines.map( l => l.lineLength ).join( "," );
  }

  harmonize()
  {
    
    let maxSteps = 1000;
    let iteration = 0;

    let initialSignature = this.getLineSignature();

    while ( iteration < maxSteps )
    {
      // console.log( `Harmonize(${iteration})\n`, this.contentLines.join( "\n") );

      let inharmonyIndex = this.findBiggestInharmony();

      if ( inharmonyIndex === -1 )
      {
        // console.log( "No inharmony" );
        return;
      }

      this._harmonizeAt( inharmonyIndex );

      if ( initialSignature === this.getLineSignature() )
      {
        // console.log( "Found initialSignature" );
        return;
      }

      iteration ++;
    }
  }

  _harmonizeAt( inharmonyIndex:number )
  {
    let lineTop    = this._lines[ inharmonyIndex ];
    let lineBottom = this._lines[ inharmonyIndex + 1 ];

    if ( lineTop.lineLength > lineBottom.lineLength )
    {
      let last = lineTop.removeLast();
      lineBottom.insertAtStart( last );
      // console.log( ` Moving down:  "${last.match}"` );

    }    
    else if ( lineBottom.lineLength > lineTop.lineLength )
    {
      let first = lineBottom.removeFirst();
      lineTop.insertAtEnd( first );
      // console.log( ` Moving up:  "${first.match}"` );
    }

  }

  findBiggestInharmony()
  {
    let inharmony = 1.35;
    let inharmonyIndex = -1;

    for ( let i = 0; i < this._lines.length - 1 ; i++ )
    {
      let lineRatio = this.getMaxLineRatio( i );     
      
      if ( lineRatio > inharmony && this.getHarmonizedRatio( i ) < lineRatio )
      {
        inharmony = lineRatio;
        inharmonyIndex = i;
      }
    }

    // console.log( "Line: ", inharmonyIndex, inharmony, ">>", inharmonyIndex !== -1 ? this.getHarmonizedRatio( inharmonyIndex ) : 1 );
    return inharmonyIndex;
  }

  getHarmonizedRatio( lineIndex:number )
  {
    let wordsI = [].concat( this._lines[ lineIndex ].words );
    let wordLengthI = this._lines[ lineIndex ].lineLength;

    let wordsJ = [].concat( this._lines[ lineIndex + 1 ].words ); 
    let wordLengthJ = this._lines[ lineIndex + 1].lineLength;
    this._harmonizeAt( lineIndex );
    let harmonyRatio = this.getMaxLineRatio( lineIndex ); 

    
    this._lines[ lineIndex ].words = wordsI;
    this._lines[ lineIndex ].lineLength = wordLengthI;

    this._lines[ lineIndex + 1 ].words = wordsJ;    
    this._lines[ lineIndex + 1 ].lineLength = wordLengthJ;
    
    return harmonyRatio;

  }

  getMaxLineRatio( lineIndex:number )
  {
    let ratio = this.getLineRatio( lineIndex );

    return ratio < 1 ? ( 1 / ratio ) : ratio;
  }

  getLineRatio( lineIndex:number )
  {
    if ( lineIndex < 0 || lineIndex >= this.contentLines.length - 1 )
    {
      return -1;
    }

    return this._lines[ lineIndex ].lineLength / this._lines[ lineIndex + 1 ].lineLength;
  }

  get contentLines():string[]
  {
    return this._lines.map( l => l.lineContent );
  }
}